import React, { PureComponent } from 'react'
import Button from '@active/react-ui/Button'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import { withI18n } from '@active/react-utils/i18n'
import throttle from 'lodash/throttle'
import {
  func, string, shape,
} from 'prop-types'
import VerifyCodeByNewEmail from '../common/ProxyModalWithBodyDesc/ModalContentImplementationTemplates/VerifyCodeByNewEmail'
import { maskSenderEmail, getUrlParameter, messageUtils, getSafeReturnUrl } from '../../shared/utils/commonUtils'
import { calculateCounter } from '../common/MFAContent/MFAContent'
import EmailChangedSuccessfully from './EmailChangedSuccessfully/EmailChangedSuccessfully'
import './UpdateAccount.scss'

export class UpdateAccount extends PureComponent {
  static displayName = 'UpdatePassword'

  static propTypes = {
    i18n: func.isRequired,
    username: string.isRequired,
    stateChecker: string,
    referrer: string,
    nextTimeEmail: string,
    kcErrorMessage: string,
    setShowSideBar: func,
    services: shape({ submitChannel: func.isRequired }).isRequired,
  }

  static defaultProps = {
    nextTimeEmail: undefined,
    kcErrorMessage: undefined,
  }

  constructor(props) {
    super(props)

    const returnUrl = getSafeReturnUrl(getUrlParameter('returnUrl'))

    this.state = {
      showConfirm: false,
      countDownNumberOfSuccess: 5,
      redirectUrl: returnUrl && returnUrl !== 'undefined' ? returnUrl : window.location.href,
      countDownNumber: this.updateCount(props.nextTimeEmail),
    }
    this.proxyForm = this.proxyForm.bind(this)
    this.submitForm = this.submitForm.bind(this)
    this.handleSendCode = this.handleSendCode.bind(this)
    this.handleEmailChange = this.handleEmailChange.bind(this)
    this.updateCount = this.updateCount.bind(this)
    this.resetInfo = this.resetInfo.bind(this)
    this.setError = this.setError.bind(this)
  }

  componentDidMount() {
    const { kcErrorMessage } = this.props
    kcErrorMessage && messageUtils.alert(kcErrorMessage, { pureString: true, type: 'error', timeout: 4000 })
  }

  handleEmailChange = () => {
    const { showExistingError } = this.state
    showExistingError && this.setState({ showExistingError: false })
  }

  handleSendCode = throttle(async () => {
    const {
      services: { sendCode }, stateChecker, referrer, i18n,
    } = this.props
    this.resetInfo()
    const res = await this.form.setFieldTouched('email', true)
    if (!res.email) {
      sendCode({
        params: {
          channelValue: this.form.values.email,
          stateChecker,
          referrer,
        },
        resolve: ({
          status, nextTimeWindow, messageCode, messageContent,
        }) => {
          if (status === 'SUCCESS') {
            this.setState({ showSentCodeWording: true })
            this.updateCount(nextTimeWindow)
          } else {
            this.setError({ messageCode, messageContent })
          }
        },
        reject: ({ message: { messageContent, messageCode } = {} }) => {
          this.setError({ messageCode, messageContent, defaultError: i18n('passport.error.server.default') })
        },
      })
    }
  }, 1000, { trailing: false })

  resetInfo = () => {
    this.setState({
      showSentCodeWording: false,
      showExistingError: false,
    })
  }

  submitForm = async () => {
    const {
      services: { submitChannel }, stateChecker, referrer, setShowSideBar, i18n,
    } = this.props
    const {
      showExistingError,
    } = this.state
    const res = await this.form.validateForm({ shouldTouchFields: true })
    const { values } = this.form
    if (!(res.code || res.email || showExistingError)) {
      submitChannel({
        params: {
          channelValue: values.email,
          code: values.code,
          stateChecker,
          referrer,
        },
        resolve: ({
          status, messageContent, messageCode,
        }) => {
          if (status === 'SUCCESS') {
            this.setState({
              showConfirm: true,
            }, () => {
              setShowSideBar(false)
            })
          } else {
            this.setError({ messageCode, messageContent })
          }
        },
        reject: ({ message: { messageContent, messageCode } = {} }) => {
          this.setError({ messageCode, messageContent, defaultError: i18n('passport.error.server.default') })
        },
      })
    }
  }

  setError = ({ messageCode, messageContent, defaultError }) => {
    const errorMessage = messageContent || defaultError
    if (messageCode === 'emailExistsMessage') {
      this.setState({ showExistingError: true })
    } else {
      errorMessage && messageUtils.alert(errorMessage, { pureString: true, type: 'error', timeout: 4000 })
    }
  }

  proxyForm = form => {
    this.form = form
  }

  updateCount(nextTimeWindow) {
    if (nextTimeWindow && calculateCounter(nextTimeWindow)) {
      this.setState({
        countDownNumber: calculateCounter(nextTimeWindow),
      })
      this.timer = setInterval(() => {
        const countDownNumber = calculateCounter(nextTimeWindow)
        if (countDownNumber > 0) {
          this.setState({
            countDownNumber: countDownNumber,
          })
        } else {
          clearInterval(this.timer)
          this.setState({
            countDownNumber: undefined,
          })
        }
      }, 1000)
    }
  }

  render() {
    const {
      i18n, username,
    } = this.props
    const {
      showConfirm,
      redirectUrl,
      countDownNumberOfSuccess,
      countDownNumber,
      showSentCodeWording,
      showExistingError,
    } = this.state
    return (
      <div className="update-account-container">
        {showConfirm ? <EmailChangedSuccessfully countDownNumber={countDownNumberOfSuccess} loginSrc={redirectUrl} />
          : (
            <div className="update-account">
              <h1 className="update-account-title">
                {i18n('passport.updateAccount.title')}
              </h1>
              <Grid container={false}>
                <GridItem span={12} spanMd={8}>
                  <div className="update-account-info">
                    <div>
                      {i18n('passport.updateAccount.currentEmailLabel')}
                    </div>
                    <div>
                      {maskSenderEmail(username)}
                    </div>
                  </div>
                  <div className="update-account-content-title">
                    {i18n('passport.updateAccount.content.title')}
                  </div>
                  <div className="update-account-content-desc">
                    {i18n('passport.updateAccount.content.description')}
                  </div>
                  <div className="update-account-form-required-tips">
                    {i18n('passport.common.asterisk.significance')}
                    <span
                      className="asterisk"
                      title="Required"
                    >
                      *
                    </span>
                  </div>
                  <VerifyCodeByNewEmail
                    onEmailChange={this.handleEmailChange}
                    onSendCode={this.handleSendCode}
                    countDownNumber={countDownNumber}
                    showSentCodeWording={showSentCodeWording}
                    proxyForm={this.proxyForm}
                    showExistingError={showExistingError}
                  />
                </GridItem>
              </Grid>
              <Button type="primary" className="submit-btn" onClick={this.submitForm}>{i18n('passport.common.btn.save')}</Button>
            </div>
          )}
      </div>
    )
  }
}

export default withI18n(UpdateAccount)
